import { useEffect, useState } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../ui/dialog";
import { UserMinus, ShieldCheck, ArrowRight } from "lucide-react";
import { api, formatMoney } from "../../lib/api";
import { useLang } from "../../contexts/LangContext";

/**
 * ChurnAlertsCard — recent subscription cancellations + 90d projected impact.
 *
 * iter-211 — hidden completely when the user has NEVER had a recurring
 * receivable AND has no events. Per UX spec: don't show "0 cancellations"
 * to freelancers who don't run subscriptions.
 *
 * - Active subs + zero events → emerald "All good" card (gentle reassurance).
 * - Has events → rose-tinted card listing up to 3 + Dialog for the full list.
 * - No subs ever → returns null.
 */
export function ChurnAlertsCard() {
  const { lang } = useLang();
  const isEs = lang === "es";
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/churn", { params: { days: 30 } });
        if (alive) setState({ loading: false, data });
      } catch {
        if (alive) setState({ loading: false, data: null });
      }
    })();
  }, []);

  if (state.loading || !state.data) return null;
  const { events, total_impact_90d, count, currency, has_subscriptions } = state.data;
  // Hide ENTIRELY when the user has no active subscriptions AND no recent
  // cancellations (clean account → no need to surface the concept).
  if (!has_subscriptions && count === 0) return null;

  // Happy path: active subs, no recent churn → green reassurance card.
  if (count === 0) {
    return (
      <Card
        className="p-4 rounded-2xl border-emerald-200 bg-emerald-50/40 flex items-center gap-3"
        data-testid="churn-card-clean"
      >
        <div
          className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0"
          aria-hidden="true"
        >
          <ShieldCheck className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-800">
            {isEs ? "Churn · últimos 30 días" : "Churn · last 30 days"}
          </div>
          <div className="font-display text-sm font-semibold text-slate-900 dark:text-slate-100 mt-0.5">
            {isEs ? "Sin cancelaciones recientes." : "No cancellations this month."}
          </div>
        </div>
      </Card>
    );
  }

  // Churn detected.
  const top = events.slice(0, 3);
  return (
    <Card
      className="p-5 rounded-2xl border-rose-200 bg-gradient-to-br from-rose-50/60 to-white"
      data-testid="churn-card"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="h-10 w-10 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0"
            aria-hidden="true"
          >
            <UserMinus className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-rose-800">
              {isEs ? "Churn · últimos 30 días" : "Churn · last 30 days"}
            </div>
            <div
              className="font-display text-2xl sm:text-3xl font-bold tabular-nums mt-0.5 text-rose-700"
              data-testid="churn-card-impact"
            >
              −{formatMoney(total_impact_90d, currency)}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {count} {isEs
                ? (count === 1 ? "cancelación · impacto 90 días" : "cancelaciones · impacto 90 días")
                : (count === 1 ? "cancellation · 90d impact" : "cancellations · 90d impact")}
            </div>
          </div>
        </div>
      </div>

      <ul className="space-y-1.5" data-testid="churn-card-list">
        {top.map((e) => (
          <li
            key={e.id}
            className="flex items-center justify-between gap-2 text-xs bg-white dark:bg-slate-900/70 rounded-lg px-3 py-2 border border-rose-100"
            data-testid={`churn-row-${e.id}`}
          >
            <div className="min-w-0">
              <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
                {e.client_name}
              </div>
              {e.reason && (
                <div className="text-slate-500 dark:text-slate-400 truncate italic" title={e.reason}>
                  &ldquo;{e.reason}&rdquo;
                </div>
              )}
            </div>
            <div className="text-right flex-shrink-0">
              <div className="font-semibold tabular-nums text-rose-700">
                −{formatMoney(e.impact_90d, e.currency)}
              </div>
              <div className="text-[10px] text-slate-400">
                {new Date(e.cancelled_at).toLocaleDateString(isEs ? "es" : "en", { day: "2-digit", month: "short" })}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {count > top.length && (
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="mt-3 w-full text-rose-700 hover:bg-rose-100/60"
              data-testid="churn-card-see-all"
            >
              {isEs ? `Ver las ${count}` : `View all ${count}`}
              <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md" data-testid="churn-detail-dialog">
            <DialogHeader>
              <DialogTitle>
                {isEs ? "Cancelaciones recientes" : "Recent cancellations"}
              </DialogTitle>
            </DialogHeader>
            <ul className="space-y-2 max-h-[60vh] overflow-y-auto">
              {events.map((e) => (
                <li
                  key={e.id}
                  className="flex items-start justify-between gap-2 text-sm border-b border-slate-100 dark:border-slate-800 pb-2 last:border-0"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 dark:text-slate-100 truncate">{e.client_name}</div>
                    {e.reason && (
                      <div className="text-xs text-slate-500 dark:text-slate-400 italic mt-0.5" title={e.reason}>
                        &ldquo;{e.reason}&rdquo;
                      </div>
                    )}
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {new Date(e.cancelled_at).toLocaleDateString(isEs ? "es" : "en", {
                        day: "2-digit", month: "short", year: "numeric",
                      })}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-semibold tabular-nums text-rose-700">
                      −{formatMoney(e.impact_90d, e.currency)}
                    </div>
                    <div className="text-[10px] text-slate-400 tabular-nums">
                      {formatMoney(e.monthly_amount, e.currency)}/mo
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}
